import React, { useEffect, useMemo } from 'react'
import styled from 'styled-components'
import { useDispatch, useSelector } from 'react-redux'
import Text from 'core-system/Text'
import Loading from 'core-system/Loading'
import CreditCard from 'core-system/Icons/Accounts/CreditCard'
import GridCard from 'employee-platform/shared/components/GridCard'
import { GridContainer } from 'employee-platform/shared/styles/Grid'
import palette from 'core-system/Themes/palette'
import { AppState } from 'redux/config/store'
import { employeePlatformActions } from 'redux/employeePlatform/employeePlatformSlice'
import { transitPassActions } from 'redux/transitPass/transitPassActions'
import { BaseProgram } from 'redux/employeePlatform/employeePlatformTypes'
import {
  EmployeeTransitPass,
  TransitAgency,
} from 'redux/transitPass/transitPassTypes'
import { isEmployeeVisibleProgram } from 'employee-platform/shared/utils'
import FlexContainer from 'core-system/FlexContainer'
import TransitAgencyCard from './components/TransitAgencyCard'
import TransitPassProgramCard from './components/TransitPassProgramCard'

const Container = styled.div`
  padding: 2rem;
`

const Header = styled(FlexContainer)`
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 1rem;
`

const HeaderIconCircle = styled.div`
  width: 2.25rem;
  height: 2.25rem;
  border-radius: 50%;
  background-color: ${palette.secondary.purple100};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`

const Section = styled.div`
  margin-top: 2rem;
`

const SectionHeader = styled.div`
  margin-bottom: 1rem;
`

const EmptyState = styled(FlexContainer)`
  flex-direction: column;
  align-items: center;
  padding: 2rem;
  gap: 0.5rem;
`

const getAgenciesFromPrograms = (
  programs: BaseProgram[]
): TransitAgency[] => {
  const byId = new Map<string, TransitAgency>()
  programs.forEach((program) => {
    ;(program.transitAgencies ?? []).forEach((agency) => {
      if (!byId.has(agency.id)) {
        byId.set(agency.id, agency)
      }
    })
  })
  return Array.from(byId.values())
}

const TransitPassOverviewView = React.memo(() => {
  const dispatch = useDispatch()
  const { programs } = useSelector((state: AppState) => state.employeePlatform)
  const employeeTransitPasses = useSelector(
    (state: AppState) => state.transitPass.employeeTransitPasses
  )
  const { allPrograms } = programs

  const transitPassPrograms: BaseProgram[] = useMemo(
    () =>
      allPrograms?.TRANSIT_PASS
        ? Object.values(allPrograms.TRANSIT_PASS).filter((p) =>
            isEmployeeVisibleProgram(p.status)
          )
        : [],
    [allPrograms]
  )

  const agencies = useMemo(
    () => getAgenciesFromPrograms(transitPassPrograms),
    [transitPassPrograms]
  )

  const passesByAgencyId = useMemo(() => {
    const byAgency = new Map<string, EmployeeTransitPass>()
    ;(employeeTransitPasses ?? []).forEach((pass) => {
      byAgency.set(pass.transitAgency, pass)
    })
    return byAgency
  }, [employeeTransitPasses])

  useEffect(() => {
    if (allPrograms === null) {
      dispatch(employeePlatformActions.getAllPrograms())
    }
  }, [dispatch, allPrograms])

  useEffect(() => {
    if (employeeTransitPasses === null) {
      dispatch(transitPassActions.getEmployeeTransitPasses())
    }
  }, [dispatch, employeeTransitPasses])

  if (allPrograms === null || employeeTransitPasses === null) {
    return <Loading fullPage />
  }

  return (
    <Container>
      <Header>
        <FlexContainer alignItems='center' style={{ gap: '0.75rem' }}>
          <HeaderIconCircle>
            <CreditCard
              width={18}
              height={18}
              color={palette.secondary.purple700}
            />
          </HeaderIconCircle>
          <Text variant='h2'>Transit Pass</Text>
        </FlexContainer>
        <Text variant='action3' textColor={palette.text.secondary}>
          {transitPassPrograms.length} program
          {transitPassPrograms.length === 1 ? '' : 's'}
        </Text>
      </Header>

      {transitPassPrograms.length === 0 ? (
        <GridCard headerTitle='Transit Pass Programs'>
          <EmptyState>
            <Text variant='h4' textColor={palette.text.secondary}>
              No Transit Pass Program
            </Text>
            <Text variant='action3' textColor={palette.text.secondary}>
              You don&apos;t have access to a Transit Pass program at this time.
            </Text>
          </EmptyState>
        </GridCard>
      ) : (
        <GridContainer numColumns={2} customGridGap='1.5rem'>
          {transitPassPrograms.map((program) => (
            <TransitPassProgramCard key={program.id} program={program} />
          ))}
        </GridContainer>
      )}

      {agencies.length > 0 ? (
        <Section>
          <SectionHeader>
            <Text variant='h4' marginBottom='0.25rem'>
              Your Cards
            </Text>
            <Text variant='body1' textColor={palette.text.secondary}>
              Enter your transit card details below.
            </Text>
          </SectionHeader>
          <GridContainer numColumns={2}>
            {agencies.map((agency) => (
              <TransitAgencyCard
                key={agency.id}
                agency={agency}
                employeeTransitPass={passesByAgencyId.get(agency.id) ?? null}
              />
            ))}
          </GridContainer>
        </Section>
      ) : null}
    </Container>
  )
})

TransitPassOverviewView.displayName = 'TransitPassOverviewView'

export default TransitPassOverviewView
